import React, {useMemo} from 'react';
import {List} from '@mui/material';
import Grid from '@mui/material/Grid';
import type {ThesaurusNode} from 'services/thesaurus/types';
import {useLocalization} from 'localization';
import {assertThesaurusNodeWithAncestors} from 'services/thesaurus';
import {Card} from 'components/card';
import {Layout} from 'components/layout';
import {Spacer} from 'components/spacer';
import {TextMatch} from 'components/text';
import {useThesaurusEditStateContext} from '../contexts';
import {NodeLabel} from './NodeLabel';
import {ThesaurusNodeDetailsRelated} from './ThesaurusNodeDetailsRelated';
import {ThesaurusNoteWithRefs} from './ThesaurusNoteWithRefs';

type Props = {
  node: ThesaurusNode;
};

export const ThesaurusNodeDetailsBody: React.FC<Props> = ({node}) => {
  const {t} = useLocalization();
  const {thesaurus, searchQueryText, gotoFocusNodeId} =
    useThesaurusEditStateContext();

  const related = useMemo(() => {
    return node.related && node.related.length > 0
      ? node.related.map(rel =>
          assertThesaurusNodeWithAncestors(rel.id, thesaurus),
        )
      : undefined;
  }, [node, thesaurus]);

  return (
    <Layout>
      <List sx={{margin: 0, padding: 0}}>
        {node.alternativeLabels && node.alternativeLabels.length > 0 ? (
          <>
            <Card>
              <Grid container>
                <Grid size={6}>
                  <NodeLabel
                    label={t('extension.thesaurusEdit.details.synonyms')}
                  />
                </Grid>
                <Grid>
                  {node.alternativeLabels.map(synonym => (
                    <TextMatch
                      key={synonym}
                      match={searchQueryText}
                      variant="body2">
                      {synonym}
                    </TextMatch>
                  ))}
                </Grid>
              </Grid>
            </Card>
            <Spacer size={2} />
          </>
        ) : null}
        {node.definition ? (
          <>
            <Card>
              <NodeLabel
                label={t('extension.thesaurusEdit.details.definition')}
              />
              <TextMatch match={searchQueryText} variant="body2">
                {node.definition}
              </TextMatch>
            </Card>
            <Spacer size={2} />
          </>
        ) : null}
        {related ? (
          <>
            <Card>
              <ThesaurusNodeDetailsRelated
                label={t('extension.thesaurusEdit.details.related')}
                related={node.related}
              />
            </Card>
            <Spacer size={2} />
          </>
        ) : null}
        {node.note ? (
          <Card>
            <NodeLabel label={t('extension.thesaurusEdit.details.note')} />
            <ThesaurusNoteWithRefs
              note={node.note}
              searchQuery={searchQueryText}
              gotoNodeId={gotoFocusNodeId}
            />
          </Card>
        ) : null}
      </List>
    </Layout>
  );
};
